import { ROUTES } from "../../../constants/routes";
import { TransactionFormTab } from "../../../hooks/ui-states/useTransactionFormType";
import { TransactionType, Wallet } from "./components/types";

export type WalletType = 'BASIC' | 'CREDIT' | 'LOAN';

// Config for transaction type labels
export const TRANSACTION_TYPE_LABEL_CONFIG: Record<string, { key: string; defaultValue: string }> = {
  // Loan types
  'loan_borrow': { key: 'types.borrow', defaultValue: 'Vay' },
  'loan_lend': { key: 'types.lend', defaultValue: 'Cho vay' },
  'loan_repay': { key: 'types.repay', defaultValue: 'Trả nợ' },
  'loan_collect': { key: 'types.collect', defaultValue: 'Thu nợ' },
  'loan_borrow-expense': { key: 'types.borrow_expense', defaultValue: 'Vay chi tiêu' },
  // Credit card types
  'credit_pay_balance': { key: 'types.pay_balance', defaultValue: 'Thanh toán dư nợ' },
  'credit_convert_installment': { key: 'types.convert_installment', defaultValue: 'Chuyển đổi trả góp' },
  'credit_cash_advance': { key: 'types.cash_advance', defaultValue: 'Ứng tiền' },
  // Basic types
  'expense': { key: 'types.expense', defaultValue: 'Chi tiêu' },
  'income': { key: 'types.income', defaultValue: 'Thu nhập' },
  'transfer': { key: 'types.transfer', defaultValue: 'Chuyển khoản' },
};

// Icon types for transaction type icons
export type TransactionIconType =
  | 'chevron' | 'verticalBottom' | 'verticalTop' | 'creditCard'
  | 'minus' | 'plus' | 'transfer';

export const TRANSACTION_TYPE_ICON_CONFIG: Record<string, { iconType: TransactionIconType }> = {
  'loan_borrow': { iconType: 'verticalBottom' },
  'loan_lend': { iconType: 'verticalTop' },
  'loan_repay': { iconType: 'verticalTop' },
  'loan_collect': { iconType: 'verticalBottom' },
  'loan_borrow-expense': { iconType: 'verticalBottom' },
  'credit_pay_balance': { iconType: 'verticalBottom' },
  'credit_convert_installment': { iconType: 'creditCard' },
  'credit_cash_advance': { iconType: 'verticalTop' },
  'expense': { iconType: 'minus' },
  'income': { iconType: 'plus' },
  'transfer': { iconType: 'transfer' },
};

// Get colors based on transaction type config key
export const getTransactionTypeColors = (configKey: string, theme: any): { bgColor: string; iconColor: string } => {
  const colorConfig: Record<string, { bgColor: string; iconColor: string }> = {
    'loan_borrow': { bgColor: theme.palette.red[500], iconColor: theme.palette.common.white },
    'loan_lend': { bgColor: theme.palette.green[500], iconColor: theme.palette.common.white },
    'loan_repay': { bgColor: theme.palette.red[50], iconColor: theme.palette.red[500] },
    'loan_collect': { bgColor: theme.palette.green[500], iconColor: theme.palette.common.white },
    'loan_borrow-expense': { bgColor: theme.palette.pink[500], iconColor: theme.palette.common.white },
    'credit_pay_balance': { bgColor: theme.palette.purple[500], iconColor: theme.palette.common.white },
    'credit_convert_installment': { bgColor: theme.palette.purple[500], iconColor: theme.palette.common.white },
    'credit_cash_advance': { bgColor: theme.palette.purple[500], iconColor: theme.palette.common.white },
    'expense': { bgColor: theme.palette.pink[500], iconColor: theme.palette.common.white },
    'income': { bgColor: theme.palette.teal[500], iconColor: theme.palette.common.white },
    'transfer': { bgColor: theme.palette.grey[500], iconColor: theme.palette.common.white },
  };
  return colorConfig[configKey] || colorConfig['expense'];
};

// Get config key based on transaction type
export const getTransactionTypeConfigKey = (
  currentTab: TransactionType,
  loanType: string | null,
  creditCardType: string | null
): string => {
  if (currentTab === TransactionType.LOAN && loanType) {
    return `loan_${loanType}`;
  }
  if (currentTab === TransactionType.CREDIT_CARD && creditCardType) {
    return `credit_${creditCardType}`;
  }
  if (currentTab === TransactionType.INCOME) return 'income';
  if (currentTab === TransactionType.TRANSFER) return 'transfer';
  return 'expense';
};

// Get label config for transaction type
export const getTransactionTypeLabelConfig = (
  currentTab: TransactionType,
  loanType: string | null,
  creditCardType: string | null
): { key: string; defaultValue: string } => {
  const configKey = getTransactionTypeConfigKey(currentTab, loanType, creditCardType);
  return TRANSACTION_TYPE_LABEL_CONFIG[configKey] || TRANSACTION_TYPE_LABEL_CONFIG['expense'];
};

// Get icon config for transaction type
export const getTransactionTypeIconConfig = (
  currentTab: TransactionType,
  loanType: string | null,
  creditCardType: string | null
): { iconType: TransactionIconType } => {
  const configKey = getTransactionTypeConfigKey(currentTab, loanType, creditCardType);
  return TRANSACTION_TYPE_ICON_CONFIG[configKey] || TRANSACTION_TYPE_ICON_CONFIG['expense'];
};

// Get transaction URL based on current tab and loan type
export const getTransactionUrl = (
  currentTab: number,
  loanType: string | null,
  expenseWallet: any
): string => {
  const transactionUrlMap = new Map([
    ['borrow', ROUTES.BORROWING_TRANSACTION_DETAIL],
    ['borrow-expense', ROUTES.BORROWING_TRANSACTION_DETAIL],
    ['repay', ROUTES.REPAYMENT_TRANSACTION_DETAIL],
    ['lend', ROUTES.LENDING_TRANSACTION_DETAIL],
    ['collect', ROUTES.COLLECTING_TRANSACTION_DETAIL]
  ]);

  if (currentTab === TransactionType.EXPENSE && expenseWallet && expenseWallet.type === 'loan') {
    return transactionUrlMap.get('borrow') || ROUTES.TRANSACTION_DETAIL;
  }

  if (currentTab !== TransactionType.LOAN) return ROUTES.TRANSACTION_DETAIL;

  return loanType ? transactionUrlMap.get(loanType) || ROUTES.TRANSACTION_DETAIL : ROUTES.TRANSACTION_DETAIL;
};

// Map URL tab to TransactionType enum
export const mapUrlTabToEnum = (tab: TransactionFormTab): TransactionType => {
  const tabMap: Record<TransactionFormTab, TransactionType> = {
    'expense': TransactionType.EXPENSE,
    'income': TransactionType.INCOME,
    'loan': TransactionType.LOAN,
    'transfer': TransactionType.TRANSFER,
    'credit_card': TransactionType.CREDIT_CARD,
  };
  return tabMap[tab] ?? TransactionType.EXPENSE;
};

// Map TransactionType enum to URL tab
export const mapEnumToUrlTab = (val: TransactionType): TransactionFormTab => {
  const enumMap: Record<TransactionType, TransactionFormTab> = {
    [TransactionType.EXPENSE]: 'expense',
    [TransactionType.INCOME]: 'income',
    [TransactionType.LOAN]: 'loan',
    [TransactionType.TRANSFER]: 'transfer',
    [TransactionType.CREDIT_CARD]: 'credit_card',
  };
  return enumMap[val] ?? 'expense';
};

// Get wallet types for source wallet selector
export const getSourceWalletTypes = (
  currentTab: TransactionType,
  creditCardType: string | null
): WalletType[] | undefined => {
  if (currentTab === TransactionType.TRANSFER) return ['BASIC'];
  if (currentTab === TransactionType.CREDIT_CARD) {
    if (creditCardType === 'pay_balance') return ['BASIC'];
    if (creditCardType === 'cash_advance') return ['CREDIT'];
  }
  return undefined;
};

// Get wallet types for destination wallet selector
export const getDestinationWalletTypes = (
  currentTab: TransactionType,
  creditCardType: string | null
): WalletType[] | undefined => {
  if (currentTab === TransactionType.TRANSFER) return ['BASIC'];
  if (currentTab === TransactionType.CREDIT_CARD) {
    if (creditCardType === 'pay_balance' || creditCardType === 'convert_installment') return ['CREDIT'];
    if (creditCardType === 'cash_advance') return ['BASIC'];
  }
  return undefined;
};

// Check if transaction type has step flow
export const hasStepFlow = (
  currentTab: TransactionType,
  loanType: string | null,
  creditCardType: string | null
): boolean => {
  if (currentTab === TransactionType.EXPENSE) return true;
  if (currentTab === TransactionType.INCOME) return true;
  if (currentTab === TransactionType.TRANSFER) return true;
  if (currentTab === TransactionType.LOAN && loanType === 'borrow-expense') return true;
  if (currentTab === TransactionType.CREDIT_CARD) {
    return creditCardType === 'pay_balance' ||
           creditCardType === 'cash_advance' ||
           creditCardType === 'convert_installment';
  }
  return false;
};

// Get excluded wallet IDs for source wallet selector
export const getSourceExcludeWalletIds = (
  currentTab: TransactionType,
  destinationWallet: Wallet | null
): string[] => {
  if (currentTab === TransactionType.TRANSFER && destinationWallet?.wallet?.id) {
    return [destinationWallet.wallet.id];
  }
  return [];
};

// Get excluded wallet IDs for destination wallet selector
export const getDestinationExcludeWalletIds = (
  currentTab: TransactionType,
  sourceWallet: Wallet | null
): string[] => {
  if (currentTab === TransactionType.TRANSFER && sourceWallet?.wallet?.id) {
    return [sourceWallet.wallet.id];
  }
  return [];
};

// Get custom title for source wallet selector
export const getSourceWalletTitle = (
  currentTab: TransactionType,
  loanType: string | null,
  creditCardType: string | null,
  t: any
): string | undefined => {
  if (currentTab === TransactionType.EXPENSE) {
    return t('expense_wallet', { ns: 'transactions' });
  }
  if (currentTab === TransactionType.INCOME) {
    return t('income_wallet', { ns: 'transactions' });
  }
  if (currentTab === TransactionType.TRANSFER) {
    return t('transfer_wallet', { ns: 'transactions' });
  }
  if (currentTab === TransactionType.CREDIT_CARD) {
    if (creditCardType === 'pay_balance') {
      return t('select_transfer_wallet', { ns: 'transactions', defaultValue: 'Chọn ví chuyển tiền' });
    }
    if (creditCardType === 'cash_advance') {
      return t('credit_card_wallet', { ns: 'transactions' });
    }
  }
  if (currentTab === TransactionType.LOAN && loanType === 'borrow-expense') {
    return t('transaction_form.borrowing_source_wallet_title_expense', { ns: 'loan' });
  }
  return undefined;
};

// Get custom title for destination wallet selector
export const getDestinationWalletTitle = (
  currentTab: TransactionType,
  creditCardType: string | null,
  t: any
): string | undefined => {
  if (currentTab === TransactionType.TRANSFER) {
    return t('destination_wallet', { ns: 'transactions' });
  }
  if (currentTab === TransactionType.CREDIT_CARD) {
    if (creditCardType === 'pay_balance' || creditCardType === 'convert_installment') {
      return t('select_credit_card', { ns: 'transactions', defaultValue: 'Chọn thẻ tín dụng' });
    }
    if (creditCardType === 'cash_advance') {
      return t('income_wallet', { ns: 'transactions' });
    }
  }
  return undefined;
};
